// The Errors tab's view logic, pure so `app/mock/tests/errors-view.test.ts`
// can run it without a renderer (docs/plans/2026-09-14-admin-errors-page.md §7).
//
// The owner's requirement was "make it easy to filter out any noise", and these
// four functions are that requirement: collapse repeats into one row with a
// count, hide muted event names, match free text, and keep the mutes in the
// browser. A mute is a VIEWING convenience for the person who set it — never a
// scoping decision, which is why it lives here and not on the server (E-5).
import type { OpsErrorRow } from '../api/types'

export interface ErrorGroup {
  /** `function|event|error_class` — what "the same error" means here. */
  key: string
  function: string
  event: string
  error_class: string | undefined
  level: OpsErrorRow['level']
  kind: OpsErrorRow['kind']
  count: number
  /** ISO-8601, the oldest and newest occurrence in the window. */
  first: string
  last: string
  rows: OpsErrorRow[]
}

const groupKey = (row: OpsErrorRow): string => `${row.function}|${row.event}|${row.error_class ?? ''}`

/**
 * Identical function + event + error class within the window become one group,
 * newest last-occurrence first. A twelve-call Bedrock burst is one row.
 */
export function groupRows(rows: readonly OpsErrorRow[]): ErrorGroup[] {
  const groups = new Map<string, ErrorGroup>()
  for (const row of rows) {
    const key = groupKey(row)
    const existing = groups.get(key)
    if (existing === undefined) {
      groups.set(key, {
        key,
        function: row.function,
        event: row.event,
        error_class: row.error_class,
        level: row.level,
        kind: row.kind,
        count: 1,
        first: row.at,
        last: row.at,
        rows: [row],
      })
      continue
    }
    existing.count += 1
    existing.rows.push(row)
    if (row.at < existing.first) existing.first = row.at
    if (row.at > existing.last) existing.last = row.at
  }
  const out = [...groups.values()]
  for (const g of out) g.rows.sort((a, b) => b.at.localeCompare(a.at))
  return out.sort((a, b) => b.last.localeCompare(a.last))
}

/** Rows whose event name is not muted. */
export function applyMutes(rows: readonly OpsErrorRow[], muted: ReadonlySet<string>): OpsErrorRow[] {
  if (muted.size === 0) return [...rows]
  return rows.filter((r) => !muted.has(r.event))
}

/** The identifying fields a person would type a fragment of; case-insensitive. */
const TEXT_FIELDS = [
  'event',
  'error_class',
  'reason',
  'function',
  'upload_id',
  'invoice_id',
  'message_id',
  'export_id',
  'request_id',
  'route',
  'model_id',
] as const

export function matchesText(row: OpsErrorRow, term: string): boolean {
  const needle = term.trim().toLowerCase()
  if (needle === '') return true
  return TEXT_FIELDS.some((f) => {
    const v = row[f]
    return typeof v === 'string' && v.toLowerCase().includes(needle)
  })
}

/** One key, one JSON array of event names. Per viewer, per browser. */
export const MUTES_STORAGE_KEY = 'ap.errors.mutedEvents'

/**
 * Storage can throw (a private window, blocked site data, a preview) and can be
 * empty; both read as "no mutes". Never let a storage failure take the page down.
 */
export function readMutes(storage: Pick<Storage, 'getItem'> | undefined): Set<string> {
  try {
    const raw = storage?.getItem(MUTES_STORAGE_KEY)
    if (!raw) return new Set()
    const parsed: unknown = JSON.parse(raw)
    return new Set(Array.isArray(parsed) ? parsed.filter((v): v is string => typeof v === 'string') : [])
  } catch {
    return new Set()
  }
}

export function writeMutes(storage: Pick<Storage, 'setItem'> | undefined, muted: ReadonlySet<string>): void {
  try {
    storage?.setItem(MUTES_STORAGE_KEY, JSON.stringify([...muted].sort()))
  } catch {
    // A mute that does not persist is still applied for this page view.
  }
}
